import { View, Text } from "@react-pdf/renderer";
import { getAbnDocumentRows, IAbnDocumentRow } from "helpers/abn";
import { centsToDollars, parsePrice } from "helpers/price";
import { useMemo } from "react";
import { IAbnNotice } from "types/AbnNotice/AbnNotice";

import { styles } from "../styles";

// Column widths approximate the printed CMS-R-131 grid.
const COLUMN_WIDTHS = ["41%", "38%", "21%"];

const headerCellStyle = {
  ...styles.bodyText,
  fontFamily: "Helvetica-Bold",
  fontSize: 11,
  textAlign: "center" as const,
  marginBottom: 0,
  padding: 4,
};

// Reason cell's pull-back over the rows' empty middle column: the reason and cost widths.
const REASON_PULL_BACK = "59%";

// No bottom margin: a margin between cells leaves a gap in the column borders.
const bodyCellStyle = {
  ...styles.bodyText,
  fontSize: 10,
  marginBottom: 0,
};

/**
 * CLE-2659: the item/reason/cost grid of CMS-R-131 (Exp. 03/31/2029).
 *
 * The 2026 form drops the lettered column labels (D./E./F.) the previous revision used, and the
 * intro paragraph above the grid moved into MedicareDocument.
 */
export function ProceduresSection({
  activeAbnNotice,
}: {
  activeAbnNotice?: IAbnNotice | null;
}) {
  // One block per CPT code: its `code – description` row followed by its priced lines, as
  // checkout shows them. Snapshots signed before lines existed give one row per code.
  const blocks = useMemo(() => {
    const procedures = activeAbnNotice?.documentSnapshot?.procedures ?? [];
    return procedures.map((procedure: any) => ({
      key: procedure.cptCode,
      rows: getAbnDocumentRows([procedure]),
    }));
  }, [activeAbnNotice]);

  const reason = useMemo(() => {
    return (
      activeAbnNotice?.documentSnapshot?.reason ||
      "Medicare does not consider the procedure(s) listed above medically necessary for your condition or deems them experimental."
    );
  }, [activeAbnNotice]);

  return (
    <View
      style={{
        borderWidth: 1,
        borderColor: "#000",
        marginTop: 8,
        marginBottom: 8,
      }}
    >
      {/* Header row */}
      <View
        style={{
          flexDirection: "row",
          backgroundColor: "#d9d9d9",
          borderBottomWidth: 1,
          borderColor: "#000",
        }}
      >
        <Text
          style={{
            ...headerCellStyle,
            width: COLUMN_WIDTHS[0],
            borderRightWidth: 1,
            borderColor: "#000",
          }}
        >
          Item, test, service or care
        </Text>
        <Text
          style={{
            ...headerCellStyle,
            width: COLUMN_WIDTHS[1],
            borderRightWidth: 1,
            borderColor: "#000",
          }}
        >
          Reason Medicare may not pay
        </Text>
        <Text style={{ ...headerCellStyle, width: COLUMN_WIDTHS[2] }}>
          Estimated cost
        </Text>
      </View>

      {/* Body — one block per CPT code. The item and its cost share one flex row, so a cost
          always sits beside its own line even when a description wraps. The reason is one
          cell per code, merged over all of that code's rows: it is pulled back over the empty
          middle of the rows with a negative margin, and as a flex sibling it also sets the
          block's height when it is taller than the rows. Vertical borders are drawn on the
          reason cell so they run the full height of the block. */}
      {blocks.map(
        (block: { key: string; rows: IAbnDocumentRow[] }, index: number) => (
          <View
            key={`${block.key}-${index}`}
            wrap={false}
            style={{
              flexDirection: "row",
              borderTopWidth: index === 0 ? 0 : 1,
              borderColor: "#000",
            }}
          >
            <View style={{ width: "100%", flexShrink: 0, paddingVertical: 2 }}>
              {block.rows.map((row) => (
                <View key={row.key} style={{ flexDirection: "row" }}>
                  <Text
                    style={{
                      ...bodyCellStyle,
                      width: COLUMN_WIDTHS[0],
                      paddingVertical: 2,
                      paddingRight: 4,
                      paddingLeft: row.isLine ? 14 : 4,
                      // The `code – description` heading is bold; its lines are not.
                      fontFamily: row.isLine ? "Helvetica" : "Helvetica-Bold",
                    }}
                  >
                    {row.item}
                  </Text>
                  <View style={{ width: COLUMN_WIDTHS[1] }} />
                  <Text
                    style={{
                      ...bodyCellStyle,
                      width: COLUMN_WIDTHS[2],
                      paddingVertical: 2,
                      paddingHorizontal: 4,
                      textAlign: "right",
                    }}
                  >
                    {row.amount === null
                      ? " "
                      : parsePrice(centsToDollars(row.amount))}
                  </Text>
                </View>
              ))}
            </View>
            <Text
              style={{
                ...bodyCellStyle,
                width: COLUMN_WIDTHS[1],
                flexShrink: 0,
                marginLeft: `-${REASON_PULL_BACK}`,
                borderLeftWidth: 1,
                borderRightWidth: 1,
                borderColor: "#000",
                padding: 4,
              }}
            >
              {reason}
            </Text>
          </View>
        )
      )}
    </View>
  );
}
